import type { ComponentPropsWithRef } from "react";
import classNames from "classnames";
import { AvatarGroup } from "@design-system/Avatar/AvatarGroup";
import type { PresenceTooltipRow } from "@design-system/Tooltip/Tooltip";
import { AvatarTooltip } from "./AvatarTooltip";
import { KickAvatar } from "./KickAvatar";
import {
    getPresenceMemberKey,
    getPresenceMemberName,
    getPresenceStackOrder,
    isPresenceAssignee,
    NO_ASSIGNEE_NAME,
    type PresenceStackMember,
} from "./presenceStackOrder";
import { UserAvatar } from "./UserAvatar";
import styles from "./PresenceStack.module.scss";

export type {
    PresencePersonRole,
    PresenceStackMember,
    PresenceStackUser,
} from "./presenceStackOrder";

type AssigneeButtonProps = ComponentPropsWithRef<"button">;

interface PresenceStackProps {
    members: readonly PresenceStackMember[];
    /**
     * Makes the assignee (the pinned person or the empty slot) a button with
     * these props, e.g. a picker trigger's, so its popup anchors to that
     * avatar. A board card passes `tabIndex: -1` so the card stays the Tab stop.
     */
    assigneeButtonProps?: AssigneeButtonProps;
    /** Avatar size: the board card's 20px `sm` or the task top bar's 24px `md`. @default "sm" */
    size?: "sm" | "md";
    className?: string;
}

const toTooltipRow = (member: PresenceStackMember): PresenceTooltipRow => ({
    id: getPresenceMemberKey(member),
    name: getPresenceMemberName(member),
    ...(member.kind === "unassigned"
        ? { isLive: false }
        : { detail: member.detail, isLive: member.isLive }),
});

const getAssigneeButtonLabel = (member: PresenceStackMember) =>
    member.kind === "unassigned"
        ? NO_ASSIGNEE_NAME
        : `Assignee: ${getPresenceMemberName(member)}`;

const renderAvatarImage = (member: PresenceStackMember) => {
    switch (member.kind) {
        case "agent":
            return <KickAvatar />;
        case "unassigned":
            return (
                <span
                    role="img"
                    aria-label={NO_ASSIGNEE_NAME}
                    className={styles.emptyAvatar}
                />
            );
        case "person":
            return <UserAvatar user={member.user} />;
    }
};

/** One avatar with its presence tooltip; the assignee becomes a button when given its props. */
const renderAvatar = ({
    member,
    assigneeButtonProps,
}: {
    member: PresenceStackMember;
    assigneeButtonProps: AssigneeButtonProps | undefined;
}) => {
    const avatar = (
        <span className={styles.avatar}>{renderAvatarImage(member)}</span>
    );
    return (
        <AvatarTooltip presence={[toTooltipRow(member)]}>
            {assigneeButtonProps !== undefined && isPresenceAssignee(member) ? (
                <button
                    {...assigneeButtonProps}
                    type="button"
                    aria-label={getAssigneeButtonLabel(member)}
                    className={classNames(
                        styles.assigneeButton,
                        assigneeButtonProps.className,
                    )}
                >
                    {avatar}
                </button>
            ) : (
                avatar
            )}
        </AvatarTooltip>
    );
};

const getOverflowTooltip = (hiddenMembers: readonly PresenceStackMember[]) => ({
    presence: hiddenMembers.map(toTooltipRow),
});

/**
 * Who is on a board card, bottom right: a 20px stack, 24px with `size="md"` (the agent on top, live
 * then idle viewers, then the assignee or team at the right end), capped by
 * `getPresenceStackOrder`, with colourless initials. Each avatar has its own
 * presence tooltip and the +N chip's lists the hidden members. The stack is
 * never a control: with `assigneeButtonProps` only the assignee avatar (or
 * the empty slot) is a button, named "Assignee: {name}" or "No assignee",
 * whose hover and press tint that avatar alone. Clicks on every other avatar
 * and the chip reach the card.
 */
export const PresenceStack = ({
    members,
    assigneeButtonProps,
    size = "sm",
    className,
}: Readonly<PresenceStackProps>) => {
    if (members.length === 0) {
        return null;
    }
    const { visible, hidden } = getPresenceStackOrder(members);

    return (
        <span className={classNames(styles.root, className)} data-size={size}>
            <AvatarGroup
                items={[...visible, ...hidden]}
                getKey={getPresenceMemberKey}
                getLabel={getPresenceMemberName}
                renderAvatar={(member) =>
                    renderAvatar({ member, assigneeButtonProps })
                }
                max={visible.length}
                size={size}
                stacking="firstOnTop"
                getOverflowTooltip={getOverflowTooltip}
            />
        </span>
    );
};
